import type { ReactNode } from 'react';
import { tc } from '../lib/titlecase';

export type Span = 3 | 5 | 6 | 7 | 12;

/**
 * The reference card: 16px radius panel with an optional header (title, sub line, actions on
 * the right). Inside a `.grid` section, `span` sets the 12-column width (3/5/6/7/12).
 */
export function Card({
  title,
  sub,
  actions,
  span,
  id,
  className,
  children,
  as: Tag = 'section',
}: {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  span?: Span;
  id?: string;
  className?: string;
  children?: ReactNode;
  as?: 'section' | 'div' | 'article';
}) {
  const cls = ['card', span ? `c${span}` : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <Tag className={cls} id={id}>
      {title || sub || actions ? (
        <div className="hd">
          <div>
            {title ? <h2>{tc(title)}</h2> : null}
            {sub ? <div className="sub">{sub}</div> : null}
          </div>
          {actions ? <div className="controls">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </Tag>
  );
}

/** A 12-column grid row of cards (`<Grid><Card span={6}/>…</Grid>`). */
export function Grid({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <section className="grid" aria-label={label}>
      {children}
    </section>
  );
}

/** Page heading row (reference `.hero`): title, a short line under it, and controls on the right. */
export type Fact = { label: string; value: ReactNode; tone?: 'good' | 'bad' | 'warn' };

export function PageHeader({
  title,
  sub,
  controls,
  facts,
}: {
  title: ReactNode;
  sub?: ReactNode;
  controls?: ReactNode;
  /** A strip of key figures under the heading (label above, mono value below). */
  facts?: Fact[];
}) {
  return (
    <section className="hero">
      <div className="hero-row">
        <div>
          <h1>{tc(title)}</h1>
          {sub ? <p>{sub}</p> : null}
        </div>
        {controls ? <div className="controls">{controls}</div> : null}
      </div>
      {facts && facts.length ? (
        <dl className="hfacts">
          {facts.map((f) => (
            <div key={f.label} className={f.tone ? `f-${f.tone}` : undefined}>
              <dt>{tc(f.label)}</dt>
              <dd className="num">{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  );
}
